import { useState, useEffect } from "react";

export type ConnectionQuality = "offline" | "slow" | "online";

export const useConnectionQuality = () => {
  const [connectionQuality, setConnectionQuality] =
    useState<ConnectionQuality>("online");

  // Custom speed test function
  const testConnectionSpeed = async (thresholdMs = 2000): Promise<ConnectionQuality> => {
    const start = Date.now();
    try {
      await fetch("/favicon.ico", {
        method: "HEAD",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
        signal: AbortSignal.timeout(10000),
      });
      return Date.now() - start <= thresholdMs ? "online" : "slow";
    } catch {
      // No answer at all: the Wi-Fi may be up, but there's no internet behind it.
      return "offline";
    }
  };

  // Determine connection quality
  const determineConnectionQuality = async (): Promise<ConnectionQuality> => {
    if (!navigator.onLine) {
      return "offline";
    }

    // Check Network Information API if available
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;

    if (connection) {
      const effectiveType = connection.effectiveType;
      if (effectiveType === "slow-2g" || effectiveType === "2g") {
        return "slow";
      }
    }

    // Fallback to custom speed test
    return testConnectionSpeed();
  };

  useEffect(() => {
    const updateConnectionQuality = async () => {
      const quality = await determineConnectionQuality();
      setConnectionQuality(quality);
    };

    // Initial check
    updateConnectionQuality();

    // Listen for online/offline events
    const handleOnline = () => updateConnectionQuality();
    const handleOffline = () => setConnectionQuality("offline");

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Every 5 minutes: going offline is caught at once by the event above, and with many
    // household tablets a 30-second probe each would eat the static host's bandwidth.
    const interval = setInterval(() => {
      if (navigator.onLine) {
        updateConnectionQuality();
      }
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  return connectionQuality;
};
